/* eslint-disable react/jsx-wrap-multilines */
import { IconWrapper } from '@deprecated-ui/common/Iconmoon';
import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import { useEffect, useState } from 'react';
import Image from 'global/Image';
import { copyTextToClipboard } from 'utils/general';
import {
  effects as chatEffects,
  selectors as chatSelectors,
} from 'lib/entities/chat/slice';
import { useDispatch, useSelector } from 'react-redux';
import alertMessage from 'global/AlertMessage';
import StreamTabAnalytics from 'features/stream/analytics/StreamTabAnalytics';
import If from 'global/ErrorHandlers/If';
import ModalShareToStockbit from './ModalShareToStockbit';
import { Modal, ButtonAction } from './styled';
import LastConnectedRooms from './LastConnectedRooms';
import SearchReceiver from './SearchReceiver';
import ShareInput from './ShareInput';
import SocialMedia from './SocialMedia';
import { StreamPostData } from '../../../@types/stream';
import {
  transformReceiverPayload,
  transformRoomToReceiverSchema,
} from './utils';
import SectionList from './SectionList';
import SingleRecipient from './SingleRecipient';

interface ModalShareProps {
  sendChat: (val: any) => void;
  onCancel: () => void;
  onCopy?: () => void;
  visible: boolean;
  title: string;
  hasSearchPeople?: boolean;
  hasShareInput?: boolean;
  hasSocialMedia?: boolean;
  hasRepostButton?: boolean;
  hasCopyButton?: boolean;
  urlToShare?: string;
  defaultSharedMessage?: string;
  post?: StreamPostData;
  recipientName?: string | null;
  hasSingleRecipient?: boolean;
}

function ModalShare({
  onCancel,
  sendChat = null,
  onCopy = null,
  visible,
  title,
  post = null,
  hasSearchPeople = true,
  hasShareInput = true,
  hasSocialMedia = false,
  hasRepostButton = false,
  hasCopyButton = false,
  urlToShare = '',
  defaultSharedMessage = null,
  recipientName = null,
  hasSingleRecipient = false,
}: ModalShareProps) {
  const dispatch = useDispatch();

  const { rooms } = useSelector(chatSelectors);
  const lastConnectedRooms = transformRoomToReceiverSchema(rooms.slice(0, 4));

  const [isSearchReceiver, setIsSearchReceiver] = useState<boolean>(false);
  const [showModalRepost, setShowModalRepost] = useState<boolean>(false);
  // selected searched receivers
  const [selectedReceiver, setSelectedReceiver] = useState<Array<any>>([]);
  const [selectedReceiverId, setSelectedReceiverId] = useState([]);

  const showFooter =
    (hasRepostButton || hasCopyButton) &&
    !isSearchReceiver &&
    selectedReceiver.length === 0;

  const showInput =
    !showFooter || (hasShareInput && selectedReceiver.length > 0);
  const showSocialMedia =
    hasSocialMedia && selectedReceiver.length === 0 && !isSearchReceiver;
  const showLastConnectedRooms = !isSearchReceiver && hasSearchPeople;

  const handleSelectReceiver = (receiver, id) => {
    const receiverList = Array.from(selectedReceiver);
    const receiverIdList = Array.from(selectedReceiverId);
    const receiverIdx = selectedReceiverId.findIndex((i) => i === id);

    if (receiverIdx > -1) {
      receiverList.splice(receiverIdx, 1);
      receiverIdList.splice(receiverIdx, 1);
    } else if (receiverList.length < 20) {
      receiverList.push(receiver);
      receiverIdList.push(id);
    }

    setSelectedReceiver(receiverList);
    setSelectedReceiverId(receiverIdList);
  };

  const handleSendToChat = async (message) => {
    if (selectedReceiver.length > 0) {
      const data = {
        receivers: transformReceiverPayload(selectedReceiver),
        text: message || '',
      };

      sendChat(data);
      setSelectedReceiver([]);
      setSelectedReceiverId([]);
    }
  };

  const handleShareStockbit = () => {
    setShowModalRepost(true);
    onCancel();
  };

  const handleCopyLink = () => {
    if (onCopy) {
      onCopy();
    } else {
      copyTextToClipboard(urlToShare, () =>
        alertMessage({
          content: 'Link Copied',
          alertType: 'snackbar',
          messageType: 'success',
          hasCloseIcon: true,
        }),
      );
    }
  };

  const handleClickMoreContact = () => {
    setIsSearchReceiver(true);
    StreamTabAnalytics.common.Action.clickMoreShare();
  };

  const refreshRooms = () => {
    dispatch(
      chatEffects.getRooms({
        limit: 25,
        offset: 0,
        type: 'LIST_TYPE_FORWARD',
      }),
    );
  };

  useEffect(() => {
    if (!visible) {
      setIsSearchReceiver(false);
      setSelectedReceiver([]);
      setSelectedReceiverId([]);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && lastConnectedRooms.length === 0) {
      refreshRooms();
      setSelectedReceiver([]);
      setSelectedReceiverId([]);
    }
  }, [visible]);

  return (
    <>
      <Modal
        visible={visible}
        onCancel={onCancel}
        centered
        footer={null}
        title={
          <Text size="18px" family="bold" align="center">
            {title}
          </Text>
        }
        width={430}
        // @ts-expect-error
        id="modal-share"
        transitionName=""
        maskTransitionName=""
        destroyOnClose
      >
        <If condition={hasSingleRecipient}>
          <SingleRecipient
            message={defaultSharedMessage}
            selectedReceiverId={selectedReceiverId}
            onSelectReceiver={handleSelectReceiver}
            recipientName={recipientName}
          />
        </If>
        <If condition={!hasSingleRecipient && showLastConnectedRooms}>
          <LastConnectedRooms
            selectedReceiverId={selectedReceiverId}
            lastConnectedRooms={lastConnectedRooms}
            onSelectReceiver={handleSelectReceiver}
            onClickMore={handleClickMoreContact}
          />
        </If>
        <If condition={!showLastConnectedRooms}>
          <SearchReceiver
            selectedReceiverId={selectedReceiverId}
            onSelectReceiver={handleSelectReceiver}
          />
        </If>

        <If condition={showSocialMedia}>
          <SectionList title="Share to">
            <SocialMedia linkUrl={urlToShare} />
          </SectionList>
        </If>
        <If condition={showInput}>
          <ShareInput
            onSubmit={handleSendToChat}
            defaultMessage={defaultSharedMessage}
            isDisabled={selectedReceiver.length === 0}
          />
        </If>
        <If condition={showFooter}>
          <Flex width="100%" justify="space-between">
            <If condition={hasCopyButton}>
              <ButtonAction onClick={handleCopyLink}>
                <IconWrapper>
                  <Image
                    src="/icon/copy-link.svg"
                    alt="Copy link"
                    style={{
                      height: '14px',
                      minHeight: '14px',
                      minWidth: '14px',
                      marginRight: '10px',
                    }}
                  />
                </IconWrapper>
                <Text family="bold">Copy Link</Text>
              </ButtonAction>
            </If>
            <If condition={hasRepostButton}>
              <ButtonAction onClick={handleShareStockbit}>
                <IconWrapper>
                  <Image
                    src="/icon/repost-stream.svg"
                    alt="Copy link"
                    style={{
                      height: '14px',
                      minHeight: '14px',
                      minWidth: '14px',
                      marginRight: '10px',
                    }}
                  />
                </IconWrapper>
                <Text family="bold">Repost</Text>
              </ButtonAction>
            </If>
          </Flex>
        </If>
      </Modal>
      <ModalShareToStockbit
        post={post}
        defaultSharedText={defaultSharedMessage}
        visible={showModalRepost}
        destroyOnClose
        onCancel={() => setShowModalRepost(false)}
      />
    </>
  );
}

export default ModalShare;
